import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { ToastService } from '../../../../shared/services';
import { AnalyticsResponse } from '../../../../shared/interfaces/analytics.interface';
import { buildHeadline, buildInsightItems, buildSummaryText, InsightItem } from '../../utils/report-narrative';

/** Panel lateral (hoja inferior en móvil) con la lectura del período lista para leer, copiar o descargar. */
@Component({
  selector: 'app-summary-drawer',
  standalone: true,
  templateUrl: './summary-drawer.html',
  styleUrl: './summary-drawer.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SummaryDrawer implements OnChanges, OnDestroy {
  @Input() open = false;
  @Input() data: AnalyticsResponse | null = null;
  @Input() rangeLabel = '';
  @Input() businessName = '';
  @Input() canDownload = true;
  @Output() closed = new EventEmitter<void>();
  @Output() downloadPdf = new EventEmitter<void>();

  @ViewChild('closeBtn') closeBtn?: ElementRef<HTMLButtonElement>;

  private readonly toast = inject(ToastService);
  private returnFocusTo: HTMLElement | null = null;

  readonly copied = signal(false);
  headline = '';
  items: InsightItem[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && this.data) {
      this.headline = buildHeadline(this.data);
      this.items = buildInsightItems(this.data);
    }
    if (changes['open']) {
      if (this.open) {
        this.returnFocusTo = document.activeElement as HTMLElement | null;
        document.body.style.overflow = 'hidden';
        setTimeout(() => this.closeBtn?.nativeElement.focus());
      } else if (!changes['open'].firstChange) {
        document.body.style.overflow = '';
        this.returnFocusTo?.focus();
      }
    }
  }

  ngOnDestroy(): void {
    if (this.open) document.body.style.overflow = '';
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.open) this.close();
  }

  close(): void {
    this.closed.emit();
  }

  async copy(): Promise<void> {
    if (!this.data) return;
    try {
      await navigator.clipboard.writeText(buildSummaryText(this.data, this.businessName, this.rangeLabel));
      this.copied.set(true);
      setTimeout(() => this.copied.set(false), 2500);
    } catch {
      this.toast.show('No se pudo copiar el resumen. Selecciona el texto y cópialo manualmente.', 'error');
    }
  }
}
